type VariantJson = { id: number; sku?: string | null };
type ProductJson = { variants?: VariantJson[] };

const SEARCHSPRING_ROOT_ID = "searchspring-content";
const PRODUCT_LINK_SELECTOR = "a[href*='/products/']";

const extractHandleFromHref = (href: string): string | null => {
  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split("/");
    const productsIndex = segments.indexOf("products");
    if (productsIndex === -1) return null;
    const handle = segments[productsIndex + 1] || "";
    return handle || null;
  } catch {
    return null;
  }
};

const findProductHandleForForm = (form: HTMLFormElement): string | null => {
  const directLink = form.querySelector<HTMLAnchorElement>(
    PRODUCT_LINK_SELECTOR,
  );
  if (directLink) return extractHandleFromHref(directLink.href);

  let current: HTMLElement | null = form.parentElement;

  while (current && current.id !== SEARCHSPRING_ROOT_ID) {
    const link = current.querySelector<HTMLAnchorElement>(PRODUCT_LINK_SELECTOR);
    if (link) return extractHandleFromHref(link.href);
    current = current.parentElement;
  }

  return null;
};

const fetchProduct = async (handle: string): Promise<ProductJson | null> => {
  try {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return null;

    const data: ProductJson = await response.json();
    return data;
  } catch {
    return null;
  }
};

const findVariantSku = (
  product: ProductJson | null,
  variantIdRaw: string,
): string | null => {
  const variants = product?.variants;
  if (!variants || !variants.length) return null;

  const numericVariantId = Number(variantIdRaw);
  if (!Number.isFinite(numericVariantId)) return null;

  const rawSku = variants.find((variant) => variant.id === numericVariantId)
    ?.sku;
  const trimmed = rawSku?.trim() ?? "";

  return trimmed || null;
};

const getSkuPropertyLabel = (): string => {
  const root = document.getElementById(SEARCHSPRING_ROOT_ID);
  if (!(root instanceof HTMLElement)) return "SKU";

  const fromDataset =
    root.dataset.labelSkuPair?.trim() ??
    root.dataset.labelSku?.trim() ??
    "";

  return fromDataset || "SKU";
};

const ensureSkuPropertyInput = (
  form: HTMLFormElement,
  label: string,
  sku: string,
): void => {
  const trimmedSku = sku.trim();
  if (!trimmedSku) return;

  const name = `properties[${label}]`;

  const existingInput = form.querySelector<HTMLInputElement>(
    `input[name="${name}"]`,
  );
  if (existingInput) {
    existingInput.value = trimmedSku;
    existingInput.disabled = false;
    return;
  }

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = trimmedSku;
  form.append(input);
};

const enhanceFormBeforeSubmit = async (
  form: HTMLFormElement,
): Promise<void> => {
  const idInput = form.querySelector<HTMLInputElement>('input[name="id"]');
  if (!idInput) return;

  const variantIdRaw = idInput.value.trim();
  if (!variantIdRaw) return;

  const handle = findProductHandleForForm(form);
  if (!handle) return;

  const product = await fetchProduct(handle);
  const sku = findVariantSku(product, variantIdRaw);
  if (!sku) return;

  const label = getSkuPropertyLabel();
  ensureSkuPropertyInput(form, label, sku);

  if (label !== "SKU") {
    const legacyInput = form.querySelector<HTMLInputElement>(
      'input[name="properties[SKU]"]',
    );
    if (legacyInput) legacyInput.remove();
  }
};

const initSearchspringAddToCartSku = (): void => {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const submitButton =
        target.closest<HTMLButtonElement>('button[type="submit"]') ??
        target.closest<HTMLInputElement>('input[type="submit"]');
      if (!submitButton) return;

      const form =
        submitButton.form ?? submitButton.closest<HTMLFormElement>("form");
      if (!form) return;

      const root = document.getElementById(SEARCHSPRING_ROOT_ID);
      if (!root || !root.contains(form)) return;

      const idInput = form.querySelector<HTMLInputElement>('input[name="id"]');
      if (!idInput) return;

      event.preventDefault();
      event.stopPropagation();

      const submitForm = (): void => {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit(submitButton);
        } else {
          form.submit();
        }
      };

      void (async () => {
        try {
          await enhanceFormBeforeSubmit(form);
        } finally {
          submitForm();
        }
      })();
    },
    true,
  );
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSearchspringAddToCartSku);
} else {
  initSearchspringAddToCartSku();
}
